import WarningOutlinedIcon from '@mui/icons-material/WarningOutlined';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Container,
  Grid,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const BOIRDueDates = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="FinCEN BOI Reporting - Important Due Dates & Penalties"
          description="Stay compliant with FinCEN's BOI reporting. Learn key deadlines, avoid penalties, and file easily with TaxZerone’s secure and guided e-filing system."
          keywords=""
          canonical="resources/fincen-boir/boi-reporting-due-dates/"
          ogType="article"
          jsonLdPricing="39.99"
          jsonLdAggregateRatingValue="4.97"
          jsonLdAggregateRatingCount="3830"
        />
      }
    >
      <Container maxWidth="lg">
        <Alert
          severity="warning"
          icon={<WarningOutlinedIcon sx={{ fontSize: 44 }} />}
          sx={{ borderRadius: 3, mt: 3 }}
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={{ xs: 1, sm: 2 }}
            justifyContent="space-around"
            alignItems="center"
          >
            <Typography
              component="div"
              variant="body1"
              sx={{
                textAlign: { xs: 'left', sm: 'left' },
                fontSize: { xs: 13, sm: 14, md: 15 },
                //  color: '#ccc',
                lineHeight: 1.3,
              }}
            >
              <AlertTitle sx={{ fontWeight: 700, mb: 0 }}>
                BOI Compliance Update:
              </AlertTitle>
              No reporting is required for U.S. businesses and citizens. Only
              Foreign Reporting Companies must comply.{' '}
              <Link
                href="https://blog.taxzerone.com/posts/boi-report-fincen-revises-reporting-rules-now-focused-on-foreign-entities/"
                target="_blank"
                sx={{ color: '#0202ff' }}
              >
                See Details
              </Link>
              .
            </Typography>
          </Stack>
        </Alert>
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 5,
            mt: 5,
            textAlign: 'center',
            lineHeight: 1.2,
          }}
        >
          <span className="highlight-container">
            <span className="highlight">BOI Reporting </span>
          </span>{' '}
          Due Dates
        </Typography>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs
            items={[
              { label: 'Essential Forms', path: '/resources/essential-forms/' },
            ]}
          />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Stack
                  direction={{ xs: 'column-reverse', md: 'row' }}
                  spacing={{ xs: 1, sm: 2, md: 3 }}
                  sx={{ mb: 0 }}
                >
                  <Box />
                  {/* <Box
                    sx={{ mb: 1, textAlign: { xs: 'center', md: 'left' } }}
                  ></Box> */}
                </Stack>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  BOI Reporting - An introduction
                </Typography>

                <Typography variant="body1" id="when" sx={{ mb: 3 }}>
                  BOI reporting, officially known as Beneficial Ownership
                  Information Reporting, is a requirement by FinCEN (Financial
                  Crimes Enforcement Network). It mandates that entities
                  disclose every beneficial owner who holds an ownership
                  interest or has substantial control over the organization. For
                  more details please check{' '}
                  <Link
                    href="/resources/fincen-boir/boi-reporting-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    General Instructions for BOI Reporting
                  </Link>
                  .
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  BOI Important Dates
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Submitting your BOI report before the deadline is crucial to
                  remain compliant with FinCEN under the Corporate Transparency
                  Act (CTA). Below are the key dates and timelines for entities
                  subject to the reporting requirements.
                </Typography>
                <Box component="div" sx={{ mb: 2 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell
                            sx={{
                              width: 100,
                              fontWeight: 600,
                              borderRight: '1px solid #828282',
                            }}
                          >
                            S.No
                          </TableCell>
                          <TableCell
                            sx={{
                              width: 300,
                              fontWeight: 600,
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Condition
                          </TableCell>
                          <TableCell sx={{ width: 300, fontWeight: 600 }}>
                            Due Dates
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            1
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Entity registered before January 1, 2024
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            January 1, 2025
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#e0e0e0' }}>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            2
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Entity registered on or after January 1, 2024, and
                            before January 1, 2025
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            90 days from the date of registration
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            3
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Entity registered on or after January 1, 2025
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            30 days from the date of registration
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#e0e0e0' }}>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            4
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Any updates to the entity, such as changes in
                            beneficial owners, etc.
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            30 days after the change happened
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            5
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Any corrections to the BOI report already submitted
                          </TableCell>
                          <TableCell sx={{ textAlign: 'left' }}>
                            30 days after identifying the error
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Alert
                    severity="warning"
                    icon={false}
                    sx={{ borderRadius: 3, textAlign: 'left', mt: 2 }}
                  >
                    <Typography
                      variant="body1"
                      sx={{ fontSize: '18xp !important', mb: 1 }}
                    >
                      <b>Important Update: BOI Reporting is Required Again!</b>
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1.3 }}>
                      Beneficial Ownership Information (BOI) reporting
                      requirements are back in effect following a recent court
                      decision. Most companies must file by the new deadline of{' '}
                      <b>March 21, 2025</b>.
                    </Typography>
                    <Typography variant="body2">
                      <b> Avoid penalties</b>—
                      <Link href="/efile-boi-report/" sx={{ color: '#0202ff' }}>
                        file your BOI report
                      </Link>{' '}
                      with ease today!
                    </Typography>
                  </Alert>
                </Box>
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  justifyContent={{ xs: 'center', md: 'space-between' }}
                  alignItems="center"
                  spacing={2}
                  sx={{
                    py: 2,
                    px: { xs: 1, md: 2 },
                    borderRadius: 2,
                    backgroundColor: '#f9f9f9',
                    border: '1px solid #ccc',
                    mb: 3,
                  }}
                >
                  <Box
                    sx={{
                      flexGrow: 1,
                      textAlign: { xs: 'center', md: 'left' },
                    }}
                  >
                    <Typography
                      variant="h3"
                      sx={{ fontSize: '19px !important', fontWeight: 600 }}
                    >
                      Submit Your BOI Report Now with TaxZerone{' '}
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 0 }}>
                      Your Trusted Partner for BOI Reporting
                    </Typography>
                  </Box>
                  <Box>
                    <Button
                      variant="contained"
                      color="primary"
                      sx={{
                        textTransform: 'none',
                        boxShadow: 'none',
                        borderRadius: 2,
                      }}
                      size="large"
                      href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=boirduedates`}
                    >
                      E-File Now
                    </Button>
                  </Box>
                </Stack>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Consequences of Non-Compliance with BOI Reporting Requirements
                </Typography>
                <Typography variant="body1">
                  FinCEN is making significant efforts to notify reporting
                  companies about the importance of BOI reporting. Any entity
                  classified as a reporting company must ensure that it submits,
                  corrects, and updates its BOI reports on time.
                </Typography>

                <ul style={{ marginBottom: 30 }}>
                  <li style={{ marginBottom: 12 }}>
                    Failure to do so can lead to severe penalties, both civil
                    and criminal.
                  </li>
                  <ol>
                    <li style={{ marginBottom: 12 }}>
                      <b> Civil Penalties:</b> $500 is charged for each day the
                      violation continues, and this amount may increase based on
                      adjustments for inflation.
                    </li>
                    <li style={{ marginBottom: 12 }}>
                      <b> Criminal Penalties:</b> Fines up to $10,000 and/or
                      imprisonment for up to two years.
                    </li>
                  </ol>
                  <li style={{ marginBottom: 12 }}>
                    Individuals who intentionally delay filing, knowingly submit
                    false information, or fail to update beneficial ownership
                    details may be subject to these penalties.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Any individual (like the Beneficial Owner) who intentionally
                    delays the filing of the BOI report by withholding required
                    information from the filer responsible for filing the BOI
                    report of the entity may be subject to civil or criminal
                    penalties.
                  </li>
                </ul>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  File Your BOI Report with TaxZerone!
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  TaxZerone, your trusted partner for{' '}
                  <Link href="/efile-boi-report/" sx={{ color: '#0202ff' }}>
                    BOI report submission
                  </Link>
                  , makes the process smooth and effortless. Here&apos;s what we
                  offer:
                </Typography>
                <ul style={{ marginBottom: 30, listStyleType: 'none' }}>
                  <li style={{ marginBottom: 12 }}>
                    <b> Comprehensive BOI Reporting Solution:</b> We support all
                    types of BOI reports, including Initial, Correction, Update,
                    and Newly Exempt Entity submissions.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    <b> Prioritizing Security:</b> Our advanced security
                    protocols protect your personal information throughout the
                    filing process, ensuring your data is secure and giving you
                    peace of mind.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    <b>Invite Beneficial Owners:</b> Easily invite beneficial
                    owners to provide their information securely via our
                    dedicated portal (ZeroneVault).
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    <b> Easy E-Filing:</b> Our user-friendly e-filing system
                    makes FinCEN BOI reporting fast and hassle-free.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    <b> Guided Filing:</b> Get step-by-step guidance with clear
                    instructions and helpful tips to ensure your BOI report is
                    completed accurately and efficiently.
                  </li>
                </ul>

                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  spacing={2}
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ mb: 5, p: 2, borderRadius: 2 }}
                  className="gradient-bg"
                >
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 600,
                        mb: 0.5,
                        fontSize: '20px !important',
                      }}
                    >
                      Excited to Simplify BOI Reporting?
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0 }}>
                      File Effortlessly with TaxZerone Today!
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'right' }} id="howto">
                    <Button
                      variant="contained"
                      sx={{
                        textTransform: 'none',
                        boxShadow: 'none',
                        borderRadius: 2,
                        backgroundColor: '#fff',
                        color: '#333',
                        '&:hover, &:focus, &:active': {
                          color: '#fff',
                          backgroundColor: '#172b6b',
                        },
                      }}
                      size="large"
                      href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=boirduedates`}
                    >
                      E-File Now!
                    </Button>
                  </Box>
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 3,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              File your BOI Report with TaxZerone
            </Typography>
            <Typography
              variant="h3"
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 2,
              }}
            >
              Submit your BOI Report to FinCEN at just <b>$39.99 Per</b> EIN
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=boirduedates`}
              >
                E-file BOI Report Now!
              </Button>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default BOIRDueDates;
